import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useNavigate, useParams } from 'react-router';
import { Form } from 'softcat:components/form.tsx';
import { Spinner } from 'softcat:components/spinner.tsx';
import { useTerritory } from 'softcat:hooks/use-territory.ts';
import { LocationService } from 'softcat:services/location.ts';
import useSWR from 'swr';
import * as z from 'zod';

export const LocationsEdit = () => {
    const navigate = useNavigate();
    const { activeTerritory } = useTerritory();
    const { name = '' } = useParams<{ name: string }>();
    const [submitting, setSubmitting] = useState(false);
    const [onEditError, setOnEditError] = useState('');
    const [formValues, setFormValues] = useState({ active: false, description: '' });

    const EditLocationSchema = z.object({
        active: z.boolean(),
        description: z.string().optional(),
    });

    const {
        data: locationData,
        error: locationDataError,
        isLoading: locationDataIsLoading,
    } = useSWR({ key: `${activeTerritory}-LocationServiceFindOne`, name }, LocationService.findOne);

    useEffect(() => {
        if (locationData) {
            setFormValues({
                active: locationData.active,
                description: locationData.description,
            });
        }
    }, [locationData]);

    const onSubmit = async (values: z.infer<typeof EditLocationSchema>) => {
        setSubmitting(true);
        setOnEditError('');

        if (locationData) {
            try {
                await LocationService.update({
                    ...locationData,
                    active: values.active,
                    description: values.description,
                });

                setSubmitting(false);

                return navigate('/locations');
            } catch (error) {
                setSubmitting(false);
                setOnEditError((error as Error).message);
            }
        }
    };

    return (
        <Form<z.infer<typeof EditLocationSchema>> defaultValues={formValues} onSubmit={onSubmit} resolver={zodResolver(EditLocationSchema)}>
            <div className="space-y-3 px-4">
                <Form.Header
                    description={<FormattedMessage id={'LOCATIONS_EDIT_PAGE_DESCRIPTION'} />}
                    title={<FormattedMessage id={'LOCATIONS_EDIT_PAGE_HEADING'} />}
                />

                {locationDataError && (
                    <p className="mt-2 text-sm text-gray-700">
                        <FormattedMessage id={'LOCATIONS_EDIT_PAGE_ERROR_LOADING_DATA'} />
                    </p>
                )}

                {locationDataIsLoading && <Spinner />}

                {locationData && (
                    <>
                        <Form.StaticValue label={<FormattedMessage id={'LOCATIONS_FORM_NAME_LABEL'} />} value={locationData.name} />

                        <Form.Input label={<FormattedMessage id={'LOCATIONS_FORM_DESCRIPTION_LABEL'} />} name={'description'} />

                        <Form.Checkbox label={<FormattedMessage id={'LOCATIONS_FORM_ACTIVE_LABEL'} />} name={'active'} />

                        <Form.Footer
                            isSubmitting={submitting}
                            onCancel={() => {
                                return navigate('/locations');
                            }}
                            submitError={onEditError && <FormattedMessage id={'LOCATIONS_EDIT_PAGE_FORM_SUBMIT_ERROR'} values={{ error: onEditError }} />}
                            submitText={<FormattedMessage id={'BUTTON_UPDATE'} />}
                        />
                    </>
                )}
            </div>
        </Form>
    );
};
